/**
 * Notes Store - Pinia
 *
 * Manages user notes data and note-route relation rows.
 */

import { defineStore } from 'pinia';
import GeoMoverDB from '@/db/geoMoverDB';
import { appDebugLog } from '@/utils/appDebugLog';

function normalizeNumericId (value, fallback = null) {
  if (value === null || value === undefined || value === '') return fallback;
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function sortLinks (rows) {
  return [...rows].sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
}

export const useNotesStore = defineStore('notes', {
  state: () => ({
    allNotes: '',
    noteRouteLinks: [],
    isNoteRouteLinksLoaded: false
  }),

  getters: {
    getAllNotes: (state) => state.allNotes,
    getRouteIdsByNoteId: (state) => (noteId) => {
      const numericNoteId = Number(noteId);
      if (!Number.isFinite(numericNoteId)) return [];

      return state.noteRouteLinks
        .filter((row) => Number(row?.noteId) === numericNoteId)
        .map((row) => Number(row?.routeId))
        .filter((id) => Number.isFinite(id));
    },
    getNoteIdsByRouteId: (state) => (routeId) => {
      const numericRouteId = Number(routeId);
      if (!Number.isFinite(numericRouteId)) return [];

      return state.noteRouteLinks
        .filter((row) => Number(row?.routeId) === numericRouteId)
        .map((row) => Number(row?.noteId))
        .filter((id) => Number.isFinite(id));
    }
  },

  actions: {
    storeAllNotes (payload) {
      this.allNotes = payload;
    },

    async loadNoteRouteLinks () {
      try {
        const rows = await GeoMoverDB.findAll('noteRouteLinks');
        this.noteRouteLinks = sortLinks(Array.isArray(rows) ? rows : []);
        this.isNoteRouteLinksLoaded = true;
        return this.noteRouteLinks;
      }
      catch (error) {
        appDebugLog.error('NotesStore', 'Error loading noteRouteLinks:', error);
        this.noteRouteLinks = [];
        this.isNoteRouteLinksLoaded = false;
        return [];
      }
    },

    async linkNoteToRoute (noteId, routeId) {
      const numericNoteId = normalizeNumericId(noteId);
      const numericRouteId = normalizeNumericId(routeId);
      if (!Number.isFinite(numericNoteId) || !Number.isFinite(numericRouteId)) {
        throw new Error('Valid noteId and routeId are required.');
      }

      if (!this.isNoteRouteLinksLoaded) {
        await this.loadNoteRouteLinks();
      }

      const existing = this.noteRouteLinks.find((row) => (
        Number(row?.noteId) === numericNoteId && Number(row?.routeId) === numericRouteId
      ));
      if (existing) return existing;

      const payload = {
        noteId: numericNoteId,
        routeId: numericRouteId,
        createdAt: Date.now(),
        updatedAt: null
      };

      const id = await GeoMoverDB.addOne('noteRouteLinks', payload);
      const persisted = { id, ...payload };
      this.noteRouteLinks = sortLinks([...this.noteRouteLinks, persisted]);
      return persisted;
    },

    async unlinkNoteFromRoute (noteId, routeId) {
      const numericNoteId = normalizeNumericId(noteId);
      const numericRouteId = normalizeNumericId(routeId);
      if (!Number.isFinite(numericNoteId) || !Number.isFinite(numericRouteId)) return false;

      if (!this.isNoteRouteLinksLoaded) {
        await this.loadNoteRouteLinks();
      }

      const existing = this.noteRouteLinks.find((row) => (
        Number(row?.noteId) === numericNoteId && Number(row?.routeId) === numericRouteId
      ));
      if (!existing) return false;

      await GeoMoverDB.deleteOne('noteRouteLinks', Number(existing.id));
      this.noteRouteLinks = sortLinks(this.noteRouteLinks.filter((row) => Number(row?.id) !== Number(existing.id)));
      return true;
    },

    async unlinkAllRoutesForNote (noteId) {
      const numericNoteId = normalizeNumericId(noteId);
      if (!Number.isFinite(numericNoteId)) return 0;

      if (!this.isNoteRouteLinksLoaded) {
        await this.loadNoteRouteLinks();
      }

      const matches = this.noteRouteLinks.filter((row) => Number(row?.noteId) === numericNoteId);
      for (const row of matches) {
        await GeoMoverDB.deleteOne('noteRouteLinks', Number(row.id));
      }

      this.noteRouteLinks = sortLinks(this.noteRouteLinks.filter((row) => Number(row?.noteId) !== numericNoteId));
      return matches.length;
    },

    async unlinkAllNotesForRoute (routeId) {
      const numericRouteId = normalizeNumericId(routeId);
      if (!Number.isFinite(numericRouteId)) return 0;

      if (!this.isNoteRouteLinksLoaded) {
        await this.loadNoteRouteLinks();
      }

      const matches = this.noteRouteLinks.filter((row) => Number(row?.routeId) === numericRouteId);
      for (const row of matches) {
        await GeoMoverDB.deleteOne('noteRouteLinks', Number(row.id));
      }

      this.noteRouteLinks = sortLinks(this.noteRouteLinks.filter((row) => Number(row?.routeId) !== numericRouteId));
      return matches.length;
    }
  }
});
